import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LoadingLabel } from "@/components/ui/loading-label";
import { LocalDate } from "@/components/ui/local-date";
import { TableCell, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { formatCurrency } from "@/lib/utils";
import { Link, createFileRoute, notFound, redirect, useRouter } from "@tanstack/react-router";
import { ArrowLeft, Download, Trash2 } from "lucide-react";
import { useState } from "react";

/**
 * `/billing/$id` — a single invoice report (the "výkaz").
 *
 * A draft is editable in place and becomes an invoice in Hub's accounting
 * on submit; from then on the invoice decides whether it is paid. Reports
 * sent to Fakturoid before the switch keep their refresh / propagate.
 */

type LineItem = {
  id: string;
  description: string;
  totalHours: string;
  totalAmount: string;
  avgRate: string;
  workItem: { id: string; title: string } | null;
  customerCost: { id: string; label: string } | null;
  project: { id: string; name: string } | null;
};

type BatchEvent = {
  id: string;
  action: string;
  detail: string;
  createdAt: string;
  actor: { id: string; name: string | null } | null;
};

type BatchDetail = {
  id: string;
  status: "draft" | "processing" | "sent" | "paid" | "failed";
  periodFrom: string;
  periodTo: string;
  totalAmount: string | null;
  fakturoidInvoiceNumber: string | null;
  fakturoidInvoiceId: string | null;
  issuedOn: string | null;
  dueOn: string | null;
  paidAt: string | null;
  createdAt: string;
  customer: { id: string; name: string; fakturoidSubjectId: string | null };
  issuedInvoice: {
    id: string;
    number: string;
    origin: string;
    status: string;
    total: string;
    paidAmount: string;
    currency: string;
    dueOn: string | null;
    paidOn: string | null;
  } | null;
  items: LineItem[];
  events: BatchEvent[];
};

const statusVariant: Record<
  string,
  "default" | "secondary" | "success" | "warning" | "destructive" | "outline"
> = {
  draft: "secondary",
  processing: "default",
  sent: "warning",
  paid: "success",
  failed: "destructive",
};

/** A sent report past its due date with no payment recorded. */
function isOverdue(b: BatchDetail): boolean {
  return b.status === "sent" && !b.paidAt && b.dueOn !== null && new Date(b.dueOn) < new Date();
}

export const Route = createFileRoute("/_app/billing/$id")({
  loader: async ({ params }) => {
    try {
      return await api.get<BatchDetail>(`/api/billing/${params.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      if (err instanceof ApiError && err.status === 404) throw notFound();
      throw err;
    }
  },
  component: BillingDetailPage,
});

function BillingDetailPage() {
  const batch = Route.useLoaderData();
  const router = useRouter();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const invoice = batch.issuedInvoice;
  // Only a Fakturoid invoice the accounting hasn't taken over yet still talks to Fakturoid.
  const inFakturoid = batch.fakturoidInvoiceId !== null && invoice === null;
  const editable = batch.status === "draft" || (batch.status === "sent" && !invoice);
  const submitted = batch.fakturoidInvoiceId !== null || invoice !== null;
  const total = batch.items.reduce((s, i) => s + Number(i.totalAmount), 0);

  const submitM = useApiMutation(
    () =>
      api.post<{ fakturoidInvoiceNumber: string | null; pdfEmailed: boolean }>(
        `/api/billing/${batch.id}/submit`,
        {},
      ),
    {
      onSuccess: (result) => {
        toast({
          title: "Submitted to invoicing",
          description: `Invoice ${result.fakturoidInvoiceNumber ?? "created"}${
            result.pdfEmailed ? " — PDF emailed" : ""
          }`,
          variant: "success",
        });
        router.invalidate();
        setConfirmOpen(false);
      },
      onError: (msg) => {
        toast({ title: "Submit failed", description: msg, variant: "destructive" });
        setConfirmOpen(false);
      },
    },
  );

  const refreshM = useApiMutation(
    () =>
      api.post<{ status: string; paidAt: string | null }>(`/api/billing/${batch.id}/refresh`, {}),
    {
      onSuccess: (result) => {
        toast({
          title: "Status refreshed",
          description: result.paidAt ? "Invoice is paid." : `Invoice is ${result.status}.`,
          variant: "success",
        });
        router.invalidate();
      },
      onError: (msg) => {
        toast({ title: "Refresh failed", description: msg, variant: "destructive" });
        // The invoice may have been deleted in Fakturoid — the server
        // then resets the batch to draft, so re-read to reflect it.
        router.invalidate();
      },
    },
  );

  const propagateM = useApiMutation(() => api.post(`/api/billing/${batch.id}/propagate`, {}), {
    onSuccess: () => {
      toast({ title: "Changes propagated to Fakturoid", variant: "success" });
      router.invalidate();
    },
    onError: (msg) => {
      toast({ title: "Propagate failed", description: msg, variant: "destructive" });
      // If the invoice was deleted in Fakturoid the server resets the
      // batch to draft — re-read so the UI shows Submit again.
      router.invalidate();
    },
  });

  const deleteM = useApiMutation(() => api.delete(`/api/billing/${batch.id}`), {
    onSuccess: () => {
      toast({ title: "Draft report deleted", variant: "success" });
      router.navigate({ to: "/billing" });
    },
    onError: (msg) => {
      toast({ title: "Delete failed", description: msg, variant: "destructive" });
      setDeleteOpen(false);
    },
  });

  const anyBusy = submitM.pending || refreshM.pending || propagateM.pending || deleteM.pending;
  const submit = () => submitM.mutate();
  const refresh = () => refreshM.mutate();
  const propagate = () => propagateM.mutate();
  const deleteDraft = () => deleteM.mutate();

  // Header + empty-state scaffold only — the body rows are rendered by
  // `renderRow` (ItemRow), so these `cell`s never run. The Actions
  // column drops on read-only reports, keeping the empty-state colSpan
  // correct automatically.
  const itemColumns: Array<DataTableColumn<LineItem>> = [
    { key: "line", header: "Line", cell: () => null },
    { key: "hours", header: "Hours", headClassName: "text-right w-28", cell: () => null },
    { key: "rate", header: "Rate", headClassName: "text-right w-32", cell: () => null },
    { key: "amount", header: "Amount", headClassName: "text-right w-36", cell: () => null },
    { key: "actions", header: "", headClassName: "w-12", hidden: !editable, cell: () => null },
  ];

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/billing"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to billing
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
              {batch.customer.name}
              <Badge variant={statusVariant[batch.status] ?? "outline"}>{batch.status}</Badge>
              {isOverdue(batch) && <Badge variant="destructive">overdue</Badge>}
              {batch.paidAt && <Badge variant="success">paid</Badge>}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Period <LocalDate value={batch.periodFrom} /> — <LocalDate value={batch.periodTo} />
              {invoice ? (
                <>
                  {" · Invoice "}
                  <Link
                    to="/accounting/invoices/$id"
                    params={{ id: invoice.id }}
                    className="underline hover:text-foreground"
                  >
                    {invoice.number}
                  </Link>
                </>
              ) : (
                batch.fakturoidInvoiceNumber &&
                ` · Fakturoid invoice ${batch.fakturoidInvoiceNumber}`
              )}
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Total</div>
            <div className="text-2xl font-bold">{formatCurrency(total)}</div>
          </div>
        </div>
      </div>

      {submitted && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Payment status
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">Issued: </span>
              {batch.issuedOn ? <LocalDate value={batch.issuedOn} /> : "—"}
            </div>
            <div>
              <span className="text-muted-foreground">Due: </span>
              {batch.dueOn ? <LocalDate value={batch.dueOn} /> : "—"}
            </div>
            {invoice && invoice.status === "partially_paid" && (
              <div>
                <span className="text-muted-foreground">Received: </span>
                {formatCurrency(Number(invoice.paidAmount))} of{" "}
                {formatCurrency(Number(invoice.total))}
              </div>
            )}
            <div>
              <span className="text-muted-foreground">Paid: </span>
              {batch.paidAt ? (
                <span className="text-green-600 font-medium">
                  <LocalDate value={batch.paidAt} />
                </span>
              ) : isOverdue(batch) ? (
                <span className="text-destructive font-medium">overdue</span>
              ) : (
                "not yet"
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Line items — every row is bespoke inline-edit markup, so the
          columns exist only for the header + empty-state colSpan (the
          conditional Actions column drops via `hidden`); the rows
          themselves come from `renderRow`. */}
      <DataTable
        columns={itemColumns}
        rows={batch.items}
        rowKey={(i) => i.id}
        emptyState="This report has no line items."
        renderRow={(item) => (
          <ItemRow item={item} editable={editable} onChanged={() => router.invalidate()} />
        )}
      />

      <div className="flex flex-wrap items-center gap-2">
        {/* Worked-hours breakdown — the Netlion-branded "odpracované
            hodiny" sheet (one row per time entry). Available in any
            state, including draft, since it's our own document, not the
            Fakturoid invoice. */}
        <Button asChild variant="outline">
          <a href={`/api/billing/${batch.id}/worked-hours-pdf`}>
            <Download className="h-4 w-4 mr-1.5" />
            Worked hours PDF
          </a>
        </Button>
        {/* Same sheet, collapsed to one row per work item / overhead
            with the summed hours — no dates or clock times. For
            customers who only want per-task totals. */}
        <Button asChild variant="outline">
          <a href={`/api/billing/${batch.id}/worked-hours-pdf?summary=1`}>
            <Download className="h-4 w-4 mr-1.5" />
            Hours summary PDF
          </a>
        </Button>
        {batch.status === "draft" && (
          <Button onClick={() => setConfirmOpen(true)} disabled={batch.items.length === 0}>
            Submit to invoicing
          </Button>
        )}
        {submitted && (
          <>
            <Button asChild variant="outline">
              <a href={`/api/billing/${batch.id}/pdf`}>
                <Download className="h-4 w-4 mr-1.5" />
                Download PDF
              </a>
            </Button>
            {inFakturoid && (
              <Button variant="outline" onClick={refresh} disabled={anyBusy}>
                {refreshM.pending ? <LoadingLabel text="Refreshing" /> : "Refresh status"}
              </Button>
            )}
          </>
        )}
        {inFakturoid && batch.status === "sent" && (
          <Button variant="outline" onClick={propagate} disabled={anyBusy}>
            {propagateM.pending ? <LoadingLabel text="Propagating" /> : "Propagate changes"}
          </Button>
        )}
        {batch.status === "draft" && (
          <Button variant="destructive" onClick={() => setDeleteOpen(true)} disabled={anyBusy}>
            <Trash2 className="h-4 w-4 mr-1.5" />
            Delete draft
          </Button>
        )}
      </div>

      {batch.events.length > 0 && (
        <div className="space-y-2">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            History
          </h2>
          <ul className="divide-y rounded-lg border bg-card">
            {batch.events.map((ev) => (
              <li
                key={ev.id}
                className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 px-4 py-2 text-sm"
              >
                <span>{ev.detail}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {ev.actor?.name ?? "System"} · <LocalDate value={ev.createdAt} withTime />
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit to invoicing?</DialogTitle>
            <DialogDescription>
              This issues a real invoice for {batch.customer.name} in the accounting and emails its
              PDF. The covered time entries are marked invoiced and follow the invoice once it is
              paid. A mistake is corrected by a credit note.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={submitM.pending}
            >
              Cancel
            </Button>
            <Button onClick={submit} disabled={submitM.pending}>
              {submitM.pending ? <LoadingLabel text="Submitting" /> : "Create invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this draft report?</DialogTitle>
            <DialogDescription>
              The draft and its lines are removed. The covered time entries are released back so a
              later report can bill them. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleteM.pending}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={deleteDraft} disabled={deleteM.pending}>
              {deleteM.pending ? <LoadingLabel text="Deleting" /> : "Delete draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * One report line. When the batch is editable the description and the
 * money columns become inputs that save on blur; a task line's amount
 * is derived from hours × rate, so only cost lines edit the amount.
 */
function ItemRow({
  item,
  editable,
  onChanged,
}: {
  item: LineItem;
  editable: boolean;
  onChanged: () => void;
}) {
  const isCost = item.customerCost !== null;
  // Per-project worked-hours / accounting lines bill as hours × rate;
  // every other line carries a flat, directly-editable amount.
  const isHoursBased =
    item.project !== null && item.workItem === null && item.customerCost === null;
  const flat = !isHoursBased;
  const [description, setDescription] = useState(item.description);
  const [hours, setHours] = useState(item.totalHours);
  const [rate, setRate] = useState(item.avgRate);
  const [amount, setAmount] = useState(item.totalAmount);
  const saveItem = useApiMutation(
    (patch: Record<string, unknown>) => api.patch(`/api/billing/items/${item.id}`, patch),
    {
      onSuccess: () => onChanged(),
      onError: (msg) => toast({ title: "Edit failed", description: msg, variant: "destructive" }),
    },
  );
  const removeItem = useApiMutation(() => api.delete(`/api/billing/items/${item.id}`), {
    onSuccess: () => {
      toast({ title: "Line removed", variant: "success" });
      onChanged();
    },
    onError: (msg) => toast({ title: "Remove failed", description: msg, variant: "destructive" }),
  });
  const saving = saveItem.pending || removeItem.pending;

  const save = (patch: Record<string, unknown>) => saveItem.mutate(patch);
  const remove = () => removeItem.mutate();

  const numClass =
    "h-8 w-full rounded border border-input bg-background px-2 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring";

  if (!editable) {
    return (
      <TableRow>
        <TableCell>
          {item.description}
          {isCost ? (
            <Badge variant="outline" className="ml-2 text-xs">
              cost
            </Badge>
          ) : (
            item.project && (
              <span className="text-xs text-muted-foreground ml-2">{item.project.name}</span>
            )
          )}
        </TableCell>
        <TableCell className="text-right">
          {flat ? <span className="text-muted-foreground">—</span> : Number(item.totalHours)}
        </TableCell>
        <TableCell className="text-right">
          {flat ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            formatCurrency(Number(item.avgRate))
          )}
        </TableCell>
        <TableCell className="text-right">{formatCurrency(Number(item.totalAmount))}</TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <input
            aria-label={`Line description for ${item.description}`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => description !== item.description && save({ description })}
            className="h-8 w-full min-w-[12rem] rounded border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {isCost ? (
            <Badge variant="outline" className="shrink-0 text-xs">
              cost
            </Badge>
          ) : (
            item.project && (
              <span className="shrink-0 text-xs text-muted-foreground">{item.project.name}</span>
            )
          )}
        </div>
      </TableCell>
      <TableCell className="text-right">
        {flat ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <input
            type="number"
            min="0"
            step="0.25"
            aria-label={`Hours for ${item.description}`}
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            onBlur={() => hours !== item.totalHours && save({ totalHours: Number(hours) })}
            className={numClass}
          />
        )}
      </TableCell>
      <TableCell className="text-right">
        {flat ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <input
            type="number"
            min="0"
            step="1"
            aria-label={`Rate for ${item.description}`}
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            onBlur={() => rate !== item.avgRate && save({ avgRate: Number(rate) })}
            className={numClass}
          />
        )}
      </TableCell>
      <TableCell className="text-right">
        {flat ? (
          <input
            type="number"
            min="0"
            step="1"
            aria-label={`Amount for ${item.description}`}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onBlur={() => amount !== item.totalAmount && save({ totalAmount: Number(amount) })}
            className={numClass}
          />
        ) : (
          formatCurrency(Number(item.totalAmount))
        )}
      </TableCell>
      <TableCell>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          disabled={saving}
          onClick={remove}
          aria-label={`Remove line ${item.description}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}
